/**
 * @module AIContext
 * @description Contexto global para el asistente de inteligencia artificial de cumplimiento ISO.
 *
 * Integra el asistente conversacional basado en Gemini (Google AI) con el estado global
 * de la aplicación. Proporciona capacidades de:
 * - Chat conversacional contextualizado con los controles ISO del usuario.
 * - Análisis de brechas (gap analysis) por control.
 * - Sugerencias automáticas de evidencias y tareas para cada control.
 * - Exportación del historial de conversación.
 *
 * Dependencias externas: `src/lib/ai/geminiClient.ts`, `src/lib/ai/assistantGemini.ts`.
 * Requiere que la API key de Gemini esté configurada en Settings o en `VITE_GEMINI_API_KEY`.
 */

// =====================================================
// CONTEXT DE IA - ASISTENTE DE CUMPLIMIENTO
// =====================================================

import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';
import { useData } from './DataContext';
import { isAIConfigured, getSavedAPIKey } from '../lib/ai/geminiClient';
import { sendMessage, suggestEvidence, suggestTasks, analyzeGaps } from '../lib/ai/assistantGemini';
import type {
  AIContext as AIContextType,
  ChatMessage,
  AIResponse,
  EvidenceSuggestion,
  TaskSuggestion,
  GapAnalysis
} from '../types/ai';
import type { ISOControl } from '../types/iso';

// =====================================================
// CONTEXT
// =====================================================

/** Contexto interno del asistente IA — accesible solo mediante `useAI()`. */
const AIContext = createContext<AIContextType | undefined>(undefined);

// =====================================================
// PROVIDER
// =====================================================

interface AIProviderProps {
  children: ReactNode;
}

/**
 * Proveedor del contexto de IA. Debe ubicarse dentro de `DataProvider`
 * ya que consume `useData()` para tener acceso a los controles ISO actuales.
 *
 * Estado interno:
 * - `conversationHistory`: historial completo de mensajes usuario/asistente.
 * - `isLoading`: mutex para prevenir solicitudes simultáneas a la API de Gemini.
 * - `error`: último mensaje de error ocurrido; se limpia al iniciar cada operación.
 * - `currentControlId`: ID del control ISO activo en la conversación actual.
 *
 * @param children - Árbol de componentes React a envolver.
 */
export function AIProvider({ children }: AIProviderProps) {
  const { controls } = useData();

  // ── Estado ──────────────────────────────────────────────────────────────────
  const [conversationHistory, setConversationHistory] = useState<ChatMessage[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentControlId, setCurrentControlId] = useState<string | undefined>();

  // La API key puede venir de localStorage (configurada en Settings) o de la variable de entorno.
  const apiKeyConfigured = !!getSavedAPIKey() || !!import.meta.env.VITE_GEMINI_API_KEY;
  const isEnabled = isAIConfigured();

  // =====================================================
  // CHAT
  // =====================================================

  /**
   * Envía un mensaje al asistente IA y agrega tanto el mensaje del usuario como
   * la respuesta al historial de conversación.
   *
   * El historial previo se pasa a la API para mantener contexto conversacional
   * (multi-turn conversation). El control ISO actual, si se provee, enriquece
   * el prompt del sistema para dar respuestas más específicas.
   *
   * @param message - Texto del mensaje escrito por el usuario.
   * @param controlId - ID opcional del control ISO en contexto (enfoca la respuesta).
   * @returns Respuesta completa del asistente incluyendo sugerencias y citas.
   * @throws Error si el asistente no está configurado o si la API falla.
   */
  const sendChatMessage = useCallback(async (
    message: string,
    controlId?: string
  ): Promise<AIResponse> => {
    if (!isEnabled) {
      throw new Error('El asistente de IA no está configurado. Por favor, configura tu API key en Settings.');
    }

    setIsLoading(true);
    setError(null);
    setCurrentControlId(controlId);

    try {
      // Buscar el objeto completo del control para enriquecer el contexto del prompt
      const currentControl = controlId ? controls.find(c => c.id === controlId) : undefined;

      // Registrar el mensaje del usuario en el historial local antes de enviar a la API
      const userMessage: ChatMessage = {
        id: `msg-${Date.now()}-user`,
        role: 'user',
        content: message,
        timestamp: new Date().toISOString(),
        metadata: { controlId }
      };

      setConversationHistory(prev => [...prev, userMessage]);

      // Preparar historial para API (solo role + content, sin metadatos internos)
      const apiHistory = conversationHistory.map(msg => ({
        role: msg.role,
        content: msg.content
      }));

      // Enviar a IA
      const response = await sendMessage(
        message,
        controls,
        currentControl,
        apiHistory
      );

      // Registrar la respuesta del asistente en el historial
      const assistantMessage: ChatMessage = {
        id: `msg-${Date.now()}-assistant`,
        role: 'assistant',
        content: response.message,
        timestamp: new Date().toISOString(),
        metadata: {
          controlId,
          suggestedActions: response.suggestedActions,
          citations: response.citations
        }
      };

      setConversationHistory(prev => [...prev, assistantMessage]);

      console.log('[AI CONTEXT] ✅ Mensaje enviado y respuesta recibida');
      return response;

    } catch (err: any) {
      const errorMessage = err?.message || 'Error al comunicarse con el asistente';
      setError(errorMessage);
      console.error('[AI CONTEXT] ❌ Error en sendMessage:', err);
      throw new Error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  }, [isEnabled, controls, conversationHistory]);

  // =====================================================
  // ANÁLISIS DE CONTROL
  // =====================================================

  /**
   * Ejecuta un análisis de brechas (gap analysis) sobre un control ISO específico.
   * Identifica áreas de mejora, riesgos y recomendaciones de implementación.
   *
   * @param control - Control ISO a analizar (debe tener estado y evidencias actualizados).
   * @returns Análisis estructurado con brechas detectadas y plan de acción sugerido.
   * @throws Error si el asistente no está configurado o si el análisis falla.
   */
  const analyzeControl = useCallback(async (control: ISOControl): Promise<GapAnalysis> => {
    if (!isEnabled) {
      throw new Error('El asistente de IA no está configurado.');
    }

    setIsLoading(true);
    setError(null);

    try {
      console.log('[AI CONTEXT] 🔍 Analizando control:', control.code);
      const analysis = await analyzeGaps(control);
      console.log('[AI CONTEXT] ✅ Análisis completado');
      return analysis;
    } catch (err: any) {
      const errorMessage = err?.message || 'Error al analizar el control';
      setError(errorMessage);
      console.error('[AI CONTEXT] ❌ Error en analyzeControl:', err);
      throw new Error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  }, [isEnabled]);

  // =====================================================
  // SUGERENCIAS DE EVIDENCIAS
  // =====================================================

  /**
   * Genera sugerencias de evidencias apropiadas para demostrar el cumplimiento
   * de un control ISO determinado.
   *
   * @param control - Control ISO para el cual se desean sugerencias de evidencia.
   * @returns Lista de sugerencias de evidencia con descripción, tipo y prioridad.
   * @throws Error si el asistente no está configurado o si la solicitud falla.
   */
  const suggestEvidenceForControl = useCallback(async (
    control: ISOControl
  ): Promise<EvidenceSuggestion[]> => {
    if (!isEnabled) {
      throw new Error('El asistente de IA no está configurado.');
    }

    setIsLoading(true);
    setError(null);

    try {
      console.log('[AI CONTEXT] 📄 Sugiriendo evidencias para:', control.code);
      const suggestions = await suggestEvidence(control);
      console.log('[AI CONTEXT] ✅ Sugerencias de evidencia generadas:', suggestions.length);
      return suggestions;
    } catch (err: any) {
      const errorMessage = err?.message || 'Error al sugerir evidencias';
      setError(errorMessage);
      console.error('[AI CONTEXT] ❌ Error en suggestEvidence:', err);
      throw new Error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  }, [isEnabled]);

  // =====================================================
  // SUGERENCIAS DE TAREAS
  // =====================================================

  /**
   * Genera sugerencias de tareas concretas para implementar o mejorar el cumplimiento
   * de un control ISO determinado.
   *
   * @param control - Control ISO para el cual se desean sugerencias de tareas.
   * @returns Lista de tareas sugeridas con descripción, prioridad y esfuerzo estimado.
   * @throws Error si el asistente no está configurado o si la solicitud falla.
   */
  const suggestTasksForControl = useCallback(async (
    control: ISOControl
  ): Promise<TaskSuggestion[]> => {
    if (!isEnabled) {
      throw new Error('El asistente de IA no está configurado.');
    }

    setIsLoading(true);
    setError(null);

    try {
      console.log('[AI CONTEXT] 📋 Sugiriendo tareas para:', control.code);
      const suggestions = await suggestTasks(control);
      console.log('[AI CONTEXT] ✅ Sugerencias de tareas generadas:', suggestions.length);
      return suggestions;
    } catch (err: any) {
      const errorMessage = err?.message || 'Error al sugerir tareas';
      setError(errorMessage);
      console.error('[AI CONTEXT] ❌ Error en suggestTasks:', err);
      throw new Error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  }, [isEnabled]);

  // =====================================================
  // UTILIDADES
  // =====================================================

  /**
   * Limpia el historial de conversación y resetea el estado del chat.
   * Útil cuando el usuario inicia una nueva sesión de consultas sobre un control diferente.
   */
  const clearHistory = useCallback(() => {
    setConversationHistory([]);
    setCurrentControlId(undefined);
    setError(null);
    console.log('[AI CONTEXT] 🗑️ Historial de conversación limpiado');
  }, []);

  /**
   * Exporta el historial de conversación actual como texto plano formateado.
   * Cada mensaje incluye timestamp, rol (Usuario/Asistente) y contenido.
   *
   * @returns String con el historial completo, listo para copiar o descargar.
   */
  const exportConversation = useCallback((): string => {
    const text = conversationHistory.map(msg => {
      const role = msg.role === 'user' ? 'Usuario' : 'Asistente';
      const timestamp = new Date(msg.timestamp).toLocaleString('es-ES');
      return `[${timestamp}] ${role}:\n${msg.content}\n`;
    }).join('\n---\n\n');

    console.log('[AI CONTEXT] 📤 Conversación exportada');
    return text;
  }, [conversationHistory]);

  // =====================================================
  // CONTEXT VALUE
  // =====================================================

  const value: AIContextType = {
    // Métodos de interacción con la IA
    sendMessage: sendChatMessage,
    analyzeControl,
    suggestEvidence: suggestEvidenceForControl,
    suggestTasks: suggestTasksForControl,

    // Estado reactivo
    conversationHistory,
    isLoading,
    error,

    // Flags de configuración
    isEnabled,
    apiKeyConfigured,

    // Utilidades de conversación
    clearHistory,
    exportConversation
  };

  return (
    <AIContext.Provider value={value}>
      {children}
    </AIContext.Provider>
  );
}

// =====================================================
// HOOK
// =====================================================

/**
 * Hook para consumir el contexto del asistente IA desde cualquier componente
 * hijo de `AIProvider`.
 *
 * @throws Error si se usa fuera de `AIProvider`.
 * @returns Estado y métodos del asistente IA.
 *
 * @example
 * const { sendMessage, isLoading, isEnabled } = useAI();
 * if (!isEnabled) return <APIKeyRequired />;
 * const response = await sendMessage('¿Qué evidencias necesito para el control 5.1?', controlId);
 */
export function useAI(): AIContextType {
  const context = useContext(AIContext);
  if (!context) {
    throw new Error('useAI debe usarse dentro de AIProvider');
  }
  return context;
}
